import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Pagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material'
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material'
import { collection, getDocs, query, orderBy, where, Timestamp, doc, getDoc, limit, startAfter, QueryDocumentSnapshot } from 'firebase/firestore'
import { db } from '../../firebase'

interface PaymentTransaction {
  id: string
  userId: string
  requestId: string
  offerId: string
  orderId: string
  amountBeforeDiscount: number
  discountAmount: number
  finalAmount: number
  status: string
  paymentMethod: string
  paymentReference?: string
  couponCode?: string
  createdAt: Date
  completedAt?: Date
  // User info
  buyerName: string
  buyerPhone: string
  // Seller info
  sellerId: string
  sellerName: string
  sellerPhone: string
  // Product info
  productName: string
}

type SortField = 'createdAt' | 'finalAmount' | 'buyerName' | 'sellerName' | 'productName'
type SortOrder = 'asc' | 'desc'

const ITEMS_PER_PAGE = 20

export default function FinancialTransactionsPage() {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all')
  const [minAmountFilter, setMinAmountFilter] = useState<string>('')
  const [maxAmountFilter, setMaxAmountFilter] = useState<string>('')
  const [dateFromFilter, setDateFromFilter] = useState<string>('')
  const [dateToFilter, setDateToFilter] = useState<string>('')
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  
  // Last document for pagination
  const [pageHistory, setPageHistory] = useState<QueryDocumentSnapshot[]>([])

  useEffect(() => {
    // Reset to page 1 and clear history when filters or sorting change
    setPageHistory([])
    if (page !== 1) {
      setPage(1)
    } else {
      // If already on page 1, reload data
      loadTransactions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, statusFilter, paymentMethodFilter, dateFromFilter, dateToFilter])

  useEffect(() => {
    loadTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const loadTransactions = async () => {
    try {
      setLoading(true)
      setError(null)

      // Build base query
      let baseQuery = query(collection(db, 'payments'))

      // Apply filters
      if (statusFilter !== 'all') {
        baseQuery = query(baseQuery, where('status', '==', statusFilter))
      }
      
      if (paymentMethodFilter !== 'all') {
        baseQuery = query(baseQuery, where('paymentMethod', '==', paymentMethodFilter))
      }

      if (dateFromFilter) {
        const fromDate = Timestamp.fromDate(new Date(dateFromFilter))
        baseQuery = query(baseQuery, where('createdAt', '>=', fromDate))
      }

      if (dateToFilter) {
        const toDate = Timestamp.fromDate(new Date(dateToFilter + 'T23:59:59'))
        baseQuery = query(baseQuery, where('createdAt', '<=', toDate))
      }

      // Apply sorting
      const orderField = sortField === 'createdAt' ? 'createdAt' : 
                        sortField === 'finalAmount' ? 'finalAmount' : 'createdAt'
      let q = query(baseQuery, orderBy(orderField, sortOrder))

      // Pagination - use startAfter with the last document from previous page
      if (page > 1) {
        // Get the last document from the previous page
        const previousPageIndex = page - 2
        if (pageHistory[previousPageIndex]) {
          q = query(q, startAfter(pageHistory[previousPageIndex]), limit(ITEMS_PER_PAGE))
        } else {
          // If we don't have history for this page, we need to fetch from the beginning
          // This can happen if user jumps to a page directly
          // For now, just fetch the first page
          console.warn('No history for page', page, '- fetching from beginning')
          q = query(q, limit(ITEMS_PER_PAGE))
        }
      } else {
        // First page
        q = query(q, limit(ITEMS_PER_PAGE))
      }

      // Get count and paginated data in parallel
      const [countSnapshot, snapshot] = await Promise.all([
        getDocs(baseQuery), // Get total count
        getDocs(q) // Get paginated data
      ])
      
      setTotalCount(countSnapshot.size)
      setTotalPages(Math.ceil(countSnapshot.size / ITEMS_PER_PAGE))

      // Collect all unique IDs we need to fetch
      const userIds = new Set<string>()
      const offerIds = new Set<string>()
      const requestIds = new Set<string>()
      const sellerIds = new Set<string>()

      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data()
        if (data.userId) userIds.add(data.userId)
        if (data.offerId) offerIds.add(data.offerId)
        if (data.requestId) requestIds.add(data.requestId)
      })

      // Fetch all related data in parallel
      const [buyersMap, offersMap, requestsMap, sellersMap] = await Promise.all([
        // Fetch all buyers
        Promise.all(
          Array.from(userIds).map(async (userId) => {
            try {
              const buyerDoc = await getDoc(doc(db, 'users', userId))
              if (buyerDoc.exists()) {
                const buyerData = buyerDoc.data()
                return {
                  id: userId,
                  name: buyerData.name || 'غير معروف',
                  phone: buyerData.phone || 'غير معروف'
                }
              }
              return { id: userId, name: 'غير معروف', phone: 'غير معروف' }
            } catch (e) {
              console.error('Error fetching buyer:', userId, e)
              return { id: userId, name: 'غير معروف', phone: 'غير معروف' }
            }
          })
        ).then(results => {
          const map = new Map<string, { name: string; phone: string }>()
          results.forEach(r => map.set(r.id, { name: r.name, phone: r.phone }))
          return map
        }),

        // Fetch all offers
        Promise.all(
          Array.from(offerIds).map(async (offerId) => {
            try {
              const offerDoc = await getDoc(doc(db, 'offers', offerId))
              if (offerDoc.exists()) {
                const offerData = offerDoc.data()
                const sellerId = offerData.sellerId || ''
                return { id: offerId, sellerId }
              }
              return { id: offerId, sellerId: '' }
            } catch (e) {
              console.error('Error fetching offer:', offerId, e)
              return { id: offerId, sellerId: '' }
            }
          })
        ).then(results => {
          const map = new Map<string, string>()
          results.forEach(r => {
            map.set(r.id, r.sellerId)
            if (r.sellerId) sellerIds.add(r.sellerId)
          })
          return map
        }),

        // Fetch all requests
        Promise.all(
          Array.from(requestIds).map(async (requestId) => {
            try {
              const requestDoc = await getDoc(doc(db, 'requests', requestId))
              if (requestDoc.exists()) {
                const requestData = requestDoc.data()
                return {
                  id: requestId,
                  productName: requestData.productName || 'غير معروف'
                }
              }
              return { id: requestId, productName: 'غير معروف' }
            } catch (e) {
              console.error('Error fetching request:', requestId, e)
              return { id: requestId, productName: 'غير معروف' }
            }
          })
        ).then(results => {
          const map = new Map<string, string>()
          results.forEach(r => map.set(r.id, r.productName))
          return map
        }),

        // Fetch all sellers (after we know which sellers we need from offers)
        Promise.resolve(new Map<string, { name: string; phone: string }>())
      ])

      // Now fetch sellers after we collected their IDs from offers
      const sellersMapFinal = await Promise.all(
        Array.from(sellerIds).map(async (sellerId) => {
          try {
            const sellerDoc = await getDoc(doc(db, 'users', sellerId))
            if (sellerDoc.exists()) {
              const sellerData = sellerDoc.data()
              return {
                id: sellerId,
                name: sellerData.name || 'غير معروف',
                phone: sellerData.phone || 'غير معروف'
              }
            }
            return { id: sellerId, name: 'غير معروف', phone: 'غير معروف' }
          } catch (e) {
            console.error('Error fetching seller:', sellerId, e)
            return { id: sellerId, name: 'غير معروف', phone: 'غير معروف' }
          }
        })
      ).then(results => {
        const map = new Map<string, { name: string; phone: string }>()
        results.forEach(r => map.set(r.id, { name: r.name, phone: r.phone }))
        return map
      })

      // Build transactions data using the cached maps
      const transactionsData: PaymentTransaction[] = snapshot.docs.map(docSnap => {
        const data = docSnap.data()
        
        const buyer = buyersMap.get(data.userId) || { name: 'غير معروف', phone: 'غير معروف' }
        const sellerId = offersMap.get(data.offerId) || ''
        const seller = sellerId ? (sellersMapFinal.get(sellerId) || { name: 'غير معروف', phone: 'غير معروف' }) : { name: 'غير معروف', phone: 'غير معروف' }
        const productName = requestsMap.get(data.requestId) || 'غير معروف'

        return {
          id: docSnap.id,
          userId: data.userId || '',
          requestId: data.requestId || '',
          offerId: data.offerId || '',
          orderId: data.orderId || '',
          amountBeforeDiscount: data.amountBeforeDiscount || data.finalAmount || 0,
          discountAmount: data.discountAmount || 0,
          finalAmount: data.finalAmount || 0,
          status: data.status || 'pending',
          paymentMethod: data.paymentMethod || 'unknown',
          paymentReference: data.paymentReference,
          couponCode: data.couponCode,
          createdAt: data.createdAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
          buyerName: buyer.name,
          buyerPhone: buyer.phone,
          sellerId,
          sellerName: seller.name,
          sellerPhone: seller.phone,
          productName
        }
      })

      // Update page history for pagination
      // Store the last document of current page for next page navigation
      if (snapshot.docs.length > 0) {
        const lastDocument = snapshot.docs[snapshot.docs.length - 1]
        const newHistory = [...pageHistory]
        // Store at index (page - 1) so page 1 -> index 0, page 2 -> index 1, etc.
        newHistory[page - 1] = lastDocument
        setPageHistory(newHistory)
      }

      setTransactions(transactionsData)
    } catch (err: any) {
      console.error('Error loading transactions:', err)
      setError(err.message || 'حدث خطأ في تحميل المعاملات المالية')
    } finally {
      setLoading(false)
    }
  }

  // Filter transactions by search term
  const filteredTransactions = useMemo(() => {
    let filtered = transactions

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(t => 
        t.buyerName.toLowerCase().includes(searchLower) ||
        t.buyerPhone.includes(searchTerm) ||
        t.sellerName.toLowerCase().includes(searchLower) ||
        t.sellerPhone.includes(searchTerm) ||
        t.productName.toLowerCase().includes(searchLower) ||
        t.id.includes(searchTerm) ||
        t.orderId.includes(searchTerm) ||
        (t.paymentReference && t.paymentReference.includes(searchTerm))
      )
    }

    // Amount filters
    if (minAmountFilter) {
      const min = parseFloat(minAmountFilter)
      if (!isNaN(min)) {
        filtered = filtered.filter(t => t.finalAmount >= min)
      }
    }

    if (maxAmountFilter) {
      const max = parseFloat(maxAmountFilter)
      if (!isNaN(max)) {
        filtered = filtered.filter(t => t.finalAmount <= max)
      }
    }

    return filtered
  }, [transactions, searchTerm, minAmountFilter, maxAmountFilter])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'pending':
        return 'warning'
      case 'processing':
        return 'info'
      case 'failed':
      case 'cancelled':
        return 'error'
      case 'refunded':
        return 'default'
      default:
        return 'default'
    }
  }

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'completed': 'مكتمل',
      'pending': 'معلق',
      'processing': 'قيد المعالجة',
      'failed': 'فشل',
      'cancelled': 'ملغي',
      'refunded': 'مسترد'
    }
    return statusMap[status] || status
  }

  const getPaymentMethodText = (method: string) => {
    const methodMap: Record<string, string> = {
      'creditCard': 'بطاقة ائتمان',
      'electronicWallet': 'محفظة إلكترونية',
      'appWallet': 'محفظة التطبيق',
      'cashOnDelivery': 'الدفع عند الاستلام',
      'bankTransfer': 'تحويل بنكي',
      'testPayment': 'دفع تجريبي',
      'zero_amount_auto': 'صفر (تلقائي)'
    }
    return methodMap[method] || method
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(2)} جنيه`
  }

  const handleExport = () => {
    // Export to CSV
    const headers = ['رقم الفاتورة', 'التاريخ', 'المشتري', 'هاتف المشتري', 'التاجر', 'هاتف التاجر', 'المنتج', 'المبلغ قبل الخصم', 'الخصم', 'المبلغ النهائي', 'طريقة الدفع', 'الحالة', 'مرجع الدفع']
    const rows = filteredTransactions.map(t => [
      t.id,
      formatDate(t.createdAt),
      t.buyerName,
      t.buyerPhone,
      t.sellerName,
      t.sellerPhone,
      t.productName,
      t.amountBeforeDiscount,
      t.discountAmount,
      t.finalAmount,
      getPaymentMethodText(t.paymentMethod),
      getStatusText(t.status),
      t.paymentReference || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `المعاملات_المالية_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleResetFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setPaymentMethodFilter('all')
    setMinAmountFilter('')
    setMaxAmountFilter('')
    setDateFromFilter('')
    setDateToFilter('')
    setPage(1)
  }

  if (loading && transactions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          المعاملات المالية
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTransactions}
            sx={{ mr: 1 }}
          >
            تحديث
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            تصدير CSV
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            الفلاتر والبحث
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="بحث (اسم، هاتف، منتج، رقم فاتورة)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="الحالة"
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="completed">مكتمل</MenuItem>
                  <MenuItem value="pending">معلق</MenuItem>
                  <MenuItem value="processing">قيد المعالجة</MenuItem>
                  <MenuItem value="failed">فشل</MenuItem>
                  <MenuItem value="cancelled">ملغي</MenuItem>
                  <MenuItem value="refunded">مسترد</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>طريقة الدفع</InputLabel>
                <Select
                  value={paymentMethodFilter}
                  onChange={(e) => setPaymentMethodFilter(e.target.value)}
                  label="طريقة الدفع"
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="creditCard">بطاقة ائتمان</MenuItem>
                  <MenuItem value="electronicWallet">محفظة إلكترونية</MenuItem>
                  <MenuItem value="appWallet">محفظة التطبيق</MenuItem>
                  <MenuItem value="cashOnDelivery">الدفع عند الاستلام</MenuItem>
                  <MenuItem value="bankTransfer">تحويل بنكي</MenuItem>
                  <MenuItem value="testPayment">دفع تجريبي</MenuItem>
                  <MenuItem value="zero_amount_auto">صفر (تلقائي)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="الحد الأدنى (جنيه)"
                type="number"
                value={minAmountFilter}
                onChange={(e) => setMinAmountFilter(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="الحد الأقصى (جنيه)"
                type="number"
                value={maxAmountFilter}
                onChange={(e) => setMaxAmountFilter(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="من تاريخ"
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="إلى تاريخ"
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" onClick={handleResetFilters}>
                إعادة تعيين الفلاتر
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sorting Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>ترتيب حسب</InputLabel>
                <Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as SortField)}
                  label="ترتيب حسب"
                >
                  <MenuItem value="createdAt">التاريخ</MenuItem>
                  <MenuItem value="finalAmount">المبلغ</MenuItem>
                  <MenuItem value="buyerName">اسم المشتري</MenuItem>
                  <MenuItem value="sellerName">اسم التاجر</MenuItem>
                  <MenuItem value="productName">اسم المنتج</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>الاتجاه</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                  label="الاتجاه"
                >
                  <MenuItem value="desc">تنازلي</MenuItem>
                  <MenuItem value="asc">تصاعدي</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                إجمالي المعاملات: {totalCount} | المعروضة: {filteredTransactions.length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>رقم الفاتورة</TableCell>
                  <TableCell>التاريخ</TableCell>
                  <TableCell>المشتري</TableCell>
                  <TableCell>هاتف المشتري</TableCell>
                  <TableCell>التاجر</TableCell>
                  <TableCell>هاتف التاجر</TableCell>
                  <TableCell>المنتج</TableCell>
                  <TableCell>المبلغ قبل الخصم</TableCell>
                  <TableCell>الخصم</TableCell>
                  <TableCell>المبلغ النهائي</TableCell>
                  <TableCell>طريقة الدفع</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>مرجع الدفع</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                        لا توجد معاملات مالية
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>
                        <Tooltip title={transaction.id}>
                          <Box display="flex" alignItems="center">
                            <ReceiptIcon sx={{ mr: 1, fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {transaction.id.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                      <TableCell>{transaction.buyerName}</TableCell>
                      <TableCell>{transaction.buyerPhone}</TableCell>
                      <TableCell>{transaction.sellerName}</TableCell>
                      <TableCell>{transaction.sellerPhone}</TableCell>
                      <TableCell>{transaction.productName}</TableCell>
                      <TableCell>{formatCurrency(transaction.amountBeforeDiscount)}</TableCell>
                      <TableCell>
                        {transaction.discountAmount > 0 ? (
                          <Typography color="success.main">
                            -{formatCurrency(transaction.discountAmount)}
                          </Typography>
                        ) : (
                          formatCurrency(0)
                        )}
                        {transaction.couponCode && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            كوبون: {transaction.couponCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color="primary">
                          {formatCurrency(transaction.finalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getPaymentMethodText(transaction.paymentMethod)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(transaction.status)}
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {transaction.paymentReference ? (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {transaction.paymentReference}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

